import Link from 'next/link';
import styled from 'styled-components';
import snakeCase from 'lodash/snakeCase';
import { useState } from 'react';

import { IFooterData } from 'layout/footer/types/FooterData';
import { UnorderedList } from 'shared/components/UnorderedList';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useOutsideClick } from 'shared/hooks/useOutsideClick';

export const FooterNavigationMobile = ({ footerLinks }: { footerLinks: Array<IFooterData> }) => {
  const [activeLink, setActiveLink] = useState(null);
  const section = footerLinks.find((link) => link.title === activeLink);

  const hideSubLinks = () => setActiveLink(null);
  const footerLinkRef = useOutsideClick(hideSubLinks);

  const handleNavClick = (title) => {
    if (activeLink === title) {
      hideSubLinks();
    } else {
      setActiveLink(title);
    }
  };

  const handleBreadcrumbClick = (name: string) => {
    sendTrackingEvent('NAVIGATION_FOOTER_DRILLDOWN_TAP', {
      footer_title: snakeCase(activeLink),
      footer_subcategory: snakeCase(name),
    });
  };

  return (
    <FooterNavigationMobile.Wrapper ref={footerLinkRef}>
      <FooterNavigationMobile.NavWrapper>
        {footerLinks.map(({ title, id }) => {
          return (
            <FooterNavigationMobile.Nav
              data-qa={`footer_${title?.toLowerCase()}`}
              key={id}
              onClick={() => handleNavClick(title)}
              isActive={activeLink === title}
            >
              {title}
            </FooterNavigationMobile.Nav>
          );
        })}
      </FooterNavigationMobile.NavWrapper>

      {activeLink && (
        <FooterNavigationMobile.LinksWrapper>
          {section?.breadcrumbs?.map((breadcrumb) => (
            <FooterNavigationMobile.Link key={breadcrumb.id}>
              <Link href={breadcrumb.externalUrl || `/${breadcrumb.slug}`} passHref>
                <a
                  data-qa={`footer_nav_${snakeCase(breadcrumb.name)}`}
                  onClick={() => handleBreadcrumbClick(breadcrumb.name)}
                  onKeyDown={() => handleBreadcrumbClick(breadcrumb.name)}
                  role="link"
                  tabIndex={0}
                  target={breadcrumb.openInNewTab ? '_blank' : '_self'}
                  rel="noreferrer noopener"
                >
                  {breadcrumb.name}
                </a>
              </Link>
            </FooterNavigationMobile.Link>
          ))}
        </FooterNavigationMobile.LinksWrapper>
      )}
    </FooterNavigationMobile.Wrapper>
  );
};

FooterNavigationMobile.Wrapper = styled.div``;

FooterNavigationMobile.NavWrapper = styled(UnorderedList)`
  display: flex;
  margin-bottom: 9px;
`;

FooterNavigationMobile.Nav = styled.li<{ isActive?: boolean }>`
  margin-right: 16px;
  cursor: pointer;
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.darkGray : theme.navigation.textColor};

  :first-child {
    margin-left: 20px;
  }

  :active {
    text-decoration: underline;
  }

  @media (hover: hover) {
    :hover {
      text-decoration: underline;
    }
  }
`;

FooterNavigationMobile.LinksWrapper = styled(UnorderedList)`
  display: flex;
  padding: 9px 0;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch; /* for momentum scrolling */
  ${hideScrollbars};
`;

FooterNavigationMobile.Link = styled.li`
  margin-left: 20px;
  white-space: nowrap;

  :last-child {
    margin-right: 20px;
  }

  > a {
    color: ${({ theme }) => theme.navigation.textColor};

    :active {
      text-decoration: underline;
    }

    @media (hover: hover) {
      :hover {
        text-decoration: underline;
      }
    }
  }
`;
